import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Farmacia() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState('dispensar');

  return (
    <div style={{ padding: '24px 28px' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#10233B', marginBottom: 4 }}>Farmacia</h1>
      <p style={{ fontSize: 12.5, color: '#6b7a8d', marginBottom: 20 }}>
        Dispensación de recetas y validación de precios tras cada compra.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e4e9ef' }}>
        {[['dispensar', '💊 Dispensación'], ['precios', '💰 Precios pendientes']].map(([t, l]) => (
          <div key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              color: tab === t ? '#10233B' : '#8895a5',
              borderBottom: tab === t ? '2px solid #0F8B8D' : '2px solid transparent',
            }}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'dispensar' && <PanelDispensacion codRecetaInicial={params.get('receta')} codPacienteInicial={params.get('paciente')} />}
      {tab === 'precios' && <PanelPrecios />}
    </div>
  );
}

/* ── Panel de dispensación ── */
function PanelDispensacion({ codRecetaInicial, codPacienteInicial }) {
  const navigate = useNavigate();
  const [cola, setCola]               = useState([]);
  const [cargandoCola, setCargandoCola] = useState(true);
  const [busqueda, setBusqueda]       = useState('');
  const [codReceta, setCodReceta]     = useState(codRecetaInicial || '');
  const [codPaciente, setCodPaciente] = useState(codPacienteInicial || '');
  const [pacienteNombre, setPacienteNombre] = useState('');
  const [items, setItems]             = useState([]);
  const [sel, setSel]                 = useState([]); // COD_DETALLE_RECETA seleccionados
  const [historial, setHistorial]     = useState([]);
  const [anulando, setAnulando]       = useState(null); // COD_CABECERA en proceso
  const [cargando, setCargando]       = useState(false);
  const [enviando, setEnviando]       = useState(false);
  const [msg, setMsg]                 = useState('');

  const cargarCola = useCallback(async () => {
    setCargandoCola(true);
    try {
      const { data } = await api.get('/farmacia/recetas-pendientes');
      setCola(data);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setCargandoCola(false);
  }, []);

  useEffect(() => { cargarCola(); }, [cargarCola]);

  const buscar = useCallback(async (cod, pacienteCod, pacienteNom) => {
    const cr = cod ?? codReceta;
    if (!cr) return;
    setCargando(true); setMsg('');
    try {
      const { data } = await api.get(`/farmacia/receta/${cr}`);
      setItems(data);
      setSel(data.filter((i) => i.disponible).map((i) => i.COD_DETALLE_RECETA));
      if (pacienteCod) setCodPaciente(pacienteCod);
      if (pacienteNom) setPacienteNombre(pacienteNom);
      try {
        const { data: hist } = await api.get(`/farmacia/receta/${cr}/historial`);
        setHistorial(hist);
      } catch (_) { setHistorial([]); }
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
      setItems([]);
    }
    setCargando(false);
  }, [codReceta]);

  useEffect(() => { if (codRecetaInicial) buscar(codRecetaInicial); }, []); // eslint-disable-line

  const abrirReceta = (r) => {
    setCodReceta(String(r.COD_RECETA));
    buscar(r.COD_RECETA, r.COD_PACIENTE, r.PACIENTE);
  };

  const volverACola = () => {
    setCodReceta(''); setCodPaciente(''); setPacienteNombre(''); setItems([]); setSel([]); setHistorial([]); setMsg('');
    cargarCola();
  };

  const anularDispensacion = async (codCabecera) => {
    const motivo = window.prompt('Motivo de la anulación (ej: duplicado por doble clic):');
    if (motivo === null) return; // canceló
    setAnulando(codCabecera); setMsg('');
    try {
      await api.post(`/farmacia/dispensacion/${codCabecera}/anular`, { motivo });
      setMsg('✓ Dispensación anulada, stock devuelto al Kardex');
      buscar(codReceta);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setAnulando(null);
  };

  const colaFiltrada = cola.filter((r) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.trim().toLowerCase();
    return r.PACIENTE.toLowerCase().includes(q)
      || (r.IDENTIFICACION || '').includes(q)
      || String(r.COD_RECETA).includes(q);
  });

  const toggle = (cod) => setSel((s) => s.includes(cod) ? s.filter((x) => x !== cod) : [...s, cod]);

  const total = items
    .filter((i) => sel.includes(i.COD_DETALLE_RECETA))
    .reduce((acc, i) => acc + parseFloat(i.PRECIO_VENTA || 0) * parseFloat(i.CANTIDAD || 1), 0);

  const dispensar = async () => {
    if (!codPaciente) { setMsg('Falta el código del paciente'); return; }
    const paraDispensar = items.filter((i) => sel.includes(i.COD_DETALLE_RECETA));
    if (!paraDispensar.length) { setMsg('Selecciona al menos un ítem disponible'); return; }
    setEnviando(true); setMsg('');
    try {
      await api.post('/farmacia/dispensar', {
        COD_RECETA: codReceta,
        COD_PACIENTE: codPaciente,
        items: paraDispensar.map((i) => ({
          COD_DETALLE_RECETA: i.COD_DETALLE_RECETA,
          COD_MEDICAMENTO: i.COD_MEDICAMENTO,
          CANTIDAD: i.CANTIDAD,
        })),
      });
      setMsg('✓ Dispensación registrada correctamente');
      navigate(`/facturacion?paciente=${codPaciente}`);
    } catch (e) {
      setMsg('Error: ' + (e.response?.data?.error || e.message));
    }
    setEnviando(false);
  };

  // ── Vista de cola (sin receta abierta) ──
  if (!codReceta) {
    return (
      <div>
        <input placeholder="Buscar por paciente, cédula o código de receta..." value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)} autoFocus
          style={{ padding: '10px 14px', border: '1px solid #e4e9ef', borderRadius: 8, fontSize: 13, width: '100%', marginBottom: 16, boxSizing: 'border-box' }} />

        {msg && <div style={{ fontSize: 12.5, color: '#b03030', marginBottom: 14 }}>{msg}</div>}

        {cargandoCola ? (
          <div style={{ fontSize: 13, color: '#6b7a8d', textAlign: 'center', padding: 40 }}>Cargando recetas pendientes...</div>
        ) : colaFiltrada.length === 0 ? (
          <div style={{ fontSize: 13, color: '#6b7a8d', textAlign: 'center', padding: 40 }}>
            {busqueda ? 'Sin resultados para esa búsqueda.' : 'No hay recetas pendientes por despachar. Todo al día ✓'}
          </div>
        ) : (
          colaFiltrada.map((r) => (
            <div key={r.COD_RECETA} onClick={() => abrirReceta(r)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #e4e9ef',
                borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: 'pointer',
              }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#10233B' }}>{r.PACIENTE}</div>
                <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 2 }}>
                  CI {r.IDENTIFICACION} · Receta #{r.COD_RECETA} · {new Date(r.FECHA).toLocaleDateString('es-EC')}
                </div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, background: '#fdeaea', color: '#b03030', padding: '4px 10px', borderRadius: 999 }}>
                {r.ITEMS_PENDIENTES} pendiente{r.ITEMS_PENDIENTES != 1 ? 's' : ''}
              </span>
              <span style={{ fontSize: 16, color: '#8895a5' }}>→</span>
            </div>
          ))
        )}
      </div>
    );
  }

  // ── Vista de detalle (receta abierta) ──
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={volverACola}
          style={{ background: 'none', border: '1px solid #e4e9ef', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, color: '#10233B', cursor: 'pointer' }}>
          ← Volver a la cola
        </button>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#10233B' }}>{pacienteNombre || `Receta #${codReceta}`}</div>
        {cargando && <span style={{ fontSize: 12, color: '#6b7a8d' }}>Cargando...</span>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: msg.startsWith('✓') ? '#2e7d4f' : '#b03030', marginBottom: 14 }}>{msg}</div>}

      {items.map((it) => (
        <div key={it.COD_DETALLE_RECETA} style={{
          display: 'flex', alignItems: 'center', gap: 14, background: it.disponible ? '#fff' : '#fdf6f5',
          border: `1px solid ${it.disponible ? '#e4e9ef' : '#f3c9c2'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#10233B' }}>{it.NOMBRE_RECETADO}</div>
            <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 2 }}>{it.INDICACION} · Cantidad: {it.CANTIDAD}</div>
          </div>
          {it.disponible ? (
            <span style={{ fontSize: 10, fontWeight: 700, background: '#e6f4ea', color: '#2e7d4f', padding: '4px 10px', borderRadius: 999 }}>
              ✓ EN STOCK ({it.STOCK} disp.)
            </span>
          ) : (
            <span style={{ fontSize: 10, fontWeight: 700, background: '#fdeaea', color: '#b03030', padding: '4px 10px', borderRadius: 999 }}>
              {it.COD_MEDICAMENTO ? '✕ SIN STOCK' : '✕ NO EN CATÁLOGO'}
            </span>
          )}
          <div style={{ width: 70, textAlign: 'center', fontSize: 13, fontWeight: 700, color: it.disponible ? '#10233B' : '#8895a5' }}>
            {it.disponible ? `$${(parseFloat(it.PRECIO_VENTA) * parseFloat(it.CANTIDAD || 1)).toFixed(2)}` : 'Externa'}
          </div>
          <input type="checkbox" disabled={!it.disponible}
            checked={sel.includes(it.COD_DETALLE_RECETA)} onChange={() => toggle(it.COD_DETALLE_RECETA)}
            style={{ width: 18, height: 18 }} />
        </div>
      ))}

      {items.length > 0 && (
        <>
          <div style={{ height: 1, background: '#e4e9ef', margin: '18px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11.5, color: '#6b7a8d' }}>
              {sel.length} de {items.length} ítems seleccionados para dispensar
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#10233B' }}>${total.toFixed(2)}</div>
          </div>
          <button onClick={dispensar} disabled={enviando}
            style={{ marginTop: 14, width: '100%', background: '#0F8B8D', color: '#fff', border: 'none', borderRadius: 10, padding: 13, fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            {enviando ? 'Procesando...' : 'Dispensar y cobrar →'}
          </button>
        </>
      )}

      {historial.filter((h) => h.C_ESTADO === 'ACT').length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7a8d', textTransform: 'uppercase', marginBottom: 8 }}>
            Dispensaciones ya registradas
          </div>
          {historial.filter((h) => h.C_ESTADO === 'ACT').map((h) => (
            <div key={h.COD_CABECERA} style={{
              display: 'flex', alignItems: 'center', gap: 12, background: '#fafbfc', border: '1px solid #e4e9ef',
              borderRadius: 10, padding: '10px 14px', marginBottom: 8,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#10233B' }}>{h.MEDICAMENTO} · {h.CANTIDAD}</div>
                <div style={{ fontSize: 10.5, color: '#8895a5', marginTop: 2 }}>
                  {new Date(h.FECHA).toLocaleString('es-EC')} · {h.USUARIO}
                </div>
              </div>
              <button onClick={() => anularDispensacion(h.COD_CABECERA)} disabled={anulando === h.COD_CABECERA}
                style={{ background: 'none', border: '1px solid #f3c9c2', color: '#b03030', borderRadius: 8, padding: '6px 12px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                {anulando === h.COD_CABECERA ? 'Anulando...' : 'Anular'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Panel de precios pendientes ── */
function PanelPrecios() {
  const [lista, setLista]       = useState([]);
  const [editando, setEditando] = useState({}); // { [COD]: valorEscrito }
  const [msg, setMsg]           = useState('');

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get('/farmacia/precios-pendientes');
      setLista(data);
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const validar = async (cod, precio) => {
    try {
      await api.put(`/farmacia/precios/${cod}`, { PRECIO_VENTA: precio });
      setMsg('✓ Precio validado');
      cargar();
    } catch (e) { setMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  if (!lista.length) {
    return <div style={{ fontSize: 13, color: '#6b7a8d', textAlign: 'center', padding: 40 }}>
      No hay precios pendientes de validar. Todo al día ✓
    </div>;
  }

  return (
    <div>
      {msg && <div style={{ fontSize: 12.5, color: '#2e7d4f', marginBottom: 14 }}>{msg}</div>}
      {lista.map((m) => (
        <div key={m.COD} style={{
          display: 'flex', alignItems: 'center', gap: 16, background: '#fff', border: '1px solid #f0d78c',
          borderRadius: 12, padding: '14px 16px', marginBottom: 10,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: '#10233B' }}>{m.MEDICAMENTO}</div>
            <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 2 }}>Costo promedio actual: ${parseFloat(m.COSTO_PROMEDIO).toFixed(4)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, color: '#8895a5', textTransform: 'uppercase' }}>Precio actual</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#6b7a8d', textDecoration: 'line-through' }}>${parseFloat(m.PRECIO_VENTA).toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 18, color: '#8895a5' }}>→</div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 9.5, color: '#2196F3', textTransform: 'uppercase' }}>Sugerido</div>
            <input
              value={editando[m.COD] ?? m.PRECIO_SUGERIDO}
              onChange={(e) => setEditando((s) => ({ ...s, [m.COD]: e.target.value }))}
              style={{ width: 70, textAlign: 'center', fontSize: 14, fontWeight: 700, color: '#10233B', border: '1px solid #e4e9ef', borderRadius: 6, padding: '4px 6px' }}
            />
          </div>
          <button onClick={() => validar(m.COD, editando[m.COD] ?? m.PRECIO_SUGERIDO)}
            style={{ background: '#0F8B8D', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            Validar
          </button>
        </div>
      ))}
    </div>
  );
}
